import { Plus } from "phosphor-react";
import styled from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Button, ButtonLook, ButtonSize, IconButton } from "@pages/AdminDashboard/ui/Button";
import { UiKitSection } from "@pages/AdminDashboard/ui/UiKitSection";

interface LookExample {
  look: ButtonLook;
  name: string;
  label: string;
  pendingLabel: string;
}

const looks: LookExample[] = [
  { look: "primary", name: "Primary", label: "Search", pendingLabel: "Searching…" },
  { look: "commit", name: "Commit", label: "Replace Order", pendingLabel: "Replacing…" },
  { look: "secondary", name: "Secondary", label: "Refresh", pendingLabel: "Refreshing…" },
  { look: "destructive", name: "Destructive", label: "Cancel Order", pendingLabel: "Cancelling…" },
  {
    look: "destructiveOutline",
    name: "Destructive outline",
    label: "Cancel Order",
    pendingLabel: "Cancelling…",
  },
];

const sizes: { size: ButtonSize; name: string }[] = [
  { size: "standard", name: "Standard" },
  { size: "dialog", name: "Dialog" },
];

const states = ["Enabled", "Disabled", "Pending"] as const;
type ButtonState = (typeof states)[number];

const [spaceXs, , spaceMd, , spaceXl] = adminTokens.spacing;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(200px, 100%), 1fr));
  gap: ${spaceMd}px;
  margin-bottom: ${spaceXl}px;
`;

const Example = styled.div.attrs({ role: "group" })`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spaceXs}px;
  border: 1px solid ${adminTokens.color.border};
  border-radius: ${adminTokens.radius.row}px;
  padding: ${spaceMd}px;
`;

const Caption = styled.span`
  color: ${adminTokens.color.inkSoft};
  font-family: ${adminTokens.typeScale.meta.fontFamily};
  font-size: ${adminTokens.typeScale.meta.fontSize};
`;

const exampleName = (lookName: string, state: ButtonState, sizeName: string) =>
  `${lookName}, ${state}, ${sizeName}`;

const LookExampleButton = ({
  example,
  state,
  size,
}: {
  example: LookExample;
  state: ButtonState;
  size: ButtonSize;
}) => {
  if (state === "Pending") {
    return (
      <Button look={example.look} size={size} isPending pendingLabel={example.pendingLabel}>
        {example.label}
      </Button>
    );
  }

  return (
    <Button look={example.look} size={size} disabled={state === "Disabled"}>
      {example.label}
    </Button>
  );
};

// One example per look, state and height, each under its own label (BUTTON-R8).
export const ButtonSection = () => (
  <UiKitSection title="Button">
    <Grid>
      {looks.flatMap((example) =>
        states.flatMap((state) =>
          sizes.map(({ size, name }) => {
            const label = exampleName(example.name, state, name);

            return (
              <Example key={label} aria-label={label}>
                <Caption>{label}</Caption>
                <LookExampleButton example={example} state={state} size={size} />
              </Example>
            );
          })
        )
      )}
      <Example aria-label="Icon-only">
        <Caption>Icon-only</Caption>
        <IconButton look="secondary" accessibleName="Add promo code" icon={<Plus size={16} />} />
      </Example>
    </Grid>
  </UiKitSection>
);
